import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { useConnectivity } from '@/offline/useConnectivity';
import type { ApiResponse, PaginatedResponse } from '@demoops/shared-types';
import type {
  Buyer,
  ContainerType,
  DisposalVendor,
  MaterialCategory,
  MaterialType,
  ProjectScrapSummary,
  ScrapActiveJob,
  ScrapBulkPricingResult,
  ScrapDailyVolumePoint,
  ScrapDashboardToday,
  ScrapJob,
  ScrapJobUpdateData,
  ScrapJobWriteData,
  ScrapPaymentData,
  ScrapTicket,
  ScrapTicketFilters,
  ScrapTicketWriteData,
  ScrapTonnageToday,
} from '../types';

/**
 * ScrapTrack read-only hooks (Phase 13A). EVERY query is gated: `scraptrack.view`
 * pins `enabled`, so nothing fires below the grant (zero-exposure house rule).
 * Callers may also pass a local `enabled` (e.g. a gated dashboard surface) which
 * is AND-ed with the permission. `retry: false` keeps a contract-first 404/403
 * from hammering the API before BE-111 lands.
 */

const BASE = '/api/v1/scraptrack';

export const scrapKeys = {
  all: ['scraptrack'] as const,
  dashboard: (kind: string) => ['scraptrack', 'dashboard', kind] as const,
  tickets: (filters: ScrapTicketFilters) => ['scraptrack', 'tickets', filters] as const,
  ticket: (id: string) => ['scraptrack', 'ticket', id] as const,
  lookup: (kind: string) => ['scraptrack', 'lookup', kind] as const,
  admin: (kind: string) => ['scraptrack', 'admin', kind] as const,
  projectSummary: (projectId: string) => ['scraptrack', 'project-summary', projectId] as const,
};

/** True when the current user may see any ScrapTrack data. */
export function useCanViewScrapTrack(): boolean {
  const { can } = useAuth();
  return can('scraptrack.view');
}

/** May create/edit truck tickets. */
export function useCanCreateScrapTickets(): boolean {
  return useAuth().can('scraptrack.create_tickets');
}

/** May record buyer payments (pricing). */
export function useCanManageScrapPricing(): boolean {
  return useAuth().can('scraptrack.manage_pricing');
}

/** ScrapTrack administrator (can delete priced+ tickets). */
export function useIsScrapAdmin(): boolean {
  return useAuth().can('scraptrack.admin');
}

/* ————————————————————————— Dashboard ————————————————————————— */

export function useScrapDashboard(enabled = true) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.dashboard('today'),
    queryFn: async () => (await api.get<ApiResponse<ScrapDashboardToday>>(`${BASE}/dashboard/today`)).data,
    enabled: canView && enabled,
    retry: false,
  });
}

export function useScrapTonnageToday(enabled = true) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.dashboard('tonnage-today'),
    queryFn: async () => (await api.get<ApiResponse<ScrapTonnageToday>>(`${BASE}/dashboard/tonnage-today`)).data,
    enabled: canView && enabled,
    retry: false,
  });
}

export function useScrapDailyVolume(enabled = true) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.dashboard('daily-volume'),
    queryFn: async () => (await api.get<ApiResponse<ScrapDailyVolumePoint[]>>(`${BASE}/dashboard/daily-volume`)).data,
    enabled: canView && enabled,
    retry: false,
  });
}

export function useScrapActiveJobs(enabled = true) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.dashboard('active-jobs'),
    queryFn: async () => (await api.get<ApiResponse<ScrapActiveJob[]>>(`${BASE}/dashboard/active-jobs`)).data,
    enabled: canView && enabled,
    retry: false,
  });
}

/* ————————————————————————— Tickets ————————————————————————— */

export function useScrapTickets(filters: ScrapTicketFilters = {}, enabled = true) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.tickets(filters),
    queryFn: async () => {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) q.append(k, String(v));
      });
      return api.get<PaginatedResponse<ScrapTicket>>(`${BASE}/tickets/truck?${q.toString()}`);
    },
    enabled: canView && enabled,
    retry: false,
  });
}

export function useScrapTicket(id: string | undefined) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.ticket(id ?? ''),
    queryFn: async () => (await api.get<ApiResponse<ScrapTicket>>(`${BASE}/tickets/truck/${id}`)).data,
    enabled: canView && !!id,
    retry: false,
  });
}

/* ————————————————————————— Lookups ————————————————————————— */

/** Active-only reference lists (`/lookups/*`) — capture/pricing form selects. */
function useLookup<T>(kind: string, enabled: boolean) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.lookup(kind),
    queryFn: async () => (await api.get<ApiResponse<T[]>>(`${BASE}/lookups/${kind}`)).data,
    enabled: canView && enabled,
    retry: false,
    staleTime: 5 * 60 * 1000, // lookups change rarely
  });
}

export const useScrapJobs = (enabled = true) => useLookup<ScrapJob>('jobs', enabled);
export const useMaterialCategories = (enabled = true) => useLookup<MaterialCategory>('material-categories', enabled);
export const useMaterialTypes = (enabled = true) => useLookup<MaterialType>('material-types', enabled);
export const useBuyers = (enabled = true) => useLookup<Buyer>('buyers', enabled);
export const useDisposalVendors = (enabled = true) => useLookup<DisposalVendor>('disposal-vendors', enabled);
export const useContainerTypes = (enabled = true) => useLookup<ContainerType>('container-types', enabled);

/**
 * EVERY lookup the New Ticket sheet renders — the offline prefetch must warm
 * all of them or none: a partial list reproduces the half-working-form
 * failure (five live pickers and one silently empty) that made offline
 * invoice entry pointless (Michael, 2026-08-14).
 */
export const SHEET_LOOKUP_KINDS = [
  'jobs',
  'material-categories',
  'material-types',
  'buyers',
  'container-types',
  'disposal-vendors',
] as const;

/**
 * Warm the ticket sheet's six lookups whenever the app is online, so offline
 * ticket capture works on any device that has been online that day. The HTTP
 * GETs land in the service worker's 24h api-json cache — that is the durable
 * layer; the react-query entries just ride along. Self-gates on
 * scraptrack.view (everyone else costs nothing) and re-arms when a device
 * that booted offline comes online.
 */
export function useScrapLookupPrefetch(): void {
  const canView = useCanViewScrapTrack();
  const { isOnline } = useConnectivity();
  const queryClient = useQueryClient();
  const warmed = useRef(false);

  useEffect(() => {
    if (!canView || !isOnline || warmed.current) return;
    warmed.current = true;
    for (const kind of SHEET_LOOKUP_KINDS) {
      void queryClient.prefetchQuery({
        queryKey: scrapKeys.lookup(kind),
        queryFn: async () => (await api.get<ApiResponse<unknown[]>>(`${BASE}/lookups/${kind}`)).data,
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [canView, isOnline, queryClient]);
}

/* ————————————————————— Admin lookup indexes (manage page) ————————————————————— */

/**
 * Full admin indexes (`/buyers`, `/disposal-vendors`, `/container-types`,
 * `/material-types`) — ALL rows including inactive, plus ticket_count /
 * last_ticket_date where the API provides them. Reads still gate
 * `scraptrack.view` (the manage page is viewable below manage_lookups).
 */
function useAdminIndex<T>(kind: string, enabled: boolean) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.admin(kind),
    queryFn: async () => (await api.get<ApiResponse<T[]>>(`${BASE}/${kind}`)).data,
    enabled: canView && enabled,
    retry: false,
  });
}

export const useAdminScrapJobs = (enabled = true) => useAdminIndex<ScrapJob>('jobs', enabled);
export const useAdminBuyers = (enabled = true) => useAdminIndex<Buyer>('buyers', enabled);
export const useAdminDisposalVendors = (enabled = true) => useAdminIndex<DisposalVendor>('disposal-vendors', enabled);
export const useAdminContainerTypes = (enabled = true) => useAdminIndex<ContainerType>('container-types', enabled);
export const useAdminMaterialTypes = (enabled = true) => useAdminIndex<MaterialType>('material-types', enabled);

/* ————————————————————————— Job writes (parity B1) ————————————————————————— */

/**
 * A job write must refresh BOTH the active-only `/lookups/jobs` (capture +
 * price forms read it) and the full admin index behind the manage page.
 */
function invalidateJobLookups(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: scrapKeys.lookup('jobs') });
  qc.invalidateQueries({ queryKey: scrapKeys.admin('jobs') });
}

/**
 * Create a job (perm `scraptrack.create_tickets` — the ticket-form "+ Add job"
 * quick-add must work for capture users). The server rejects non-digit or
 * duplicate project numbers and duplicate names with plain 400 messages.
 */
export function useCreateScrapJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ScrapJobWriteData) =>
      (await api.post<ApiResponse<ScrapJob>>(`${BASE}/jobs`, data)).data,
    onSuccess: () => invalidateJobLookups(qc),
  });
}

/** Update a job (perm `scraptrack.manage_lookups`) — partial; Archive/Activate PUTs `status` alone. */
export function useUpdateScrapJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: ScrapJobUpdateData & { id: number }) =>
      (await api.put<ApiResponse<ScrapJob>>(`${BASE}/jobs/${id}`, data)).data,
    onSuccess: () => invalidateJobLookups(qc),
  });
}

/** Delete a job (perm `scraptrack.manage_lookups`; refused server-side when tickets exist). */
export function useDeleteScrapJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/jobs/${id}`),
    onSuccess: () => invalidateJobLookups(qc),
  });
}

/* ———————————————————— Project Scrap summary ———————————————————— */

/**
 * Per-project scrap rollup (needs projects.view + scraptrack.view server-side).
 * Gated on scraptrack.view here; a project with no matching scrap job returns
 * the empty shape (job=null) which the panel renders as a friendly empty state.
 */
export function useProjectScrapSummary(projectId: string | undefined, enabled = true) {
  const canView = useCanViewScrapTrack();
  return useQuery({
    queryKey: scrapKeys.projectSummary(projectId ?? ''),
    queryFn: async () =>
      (await api.get<ApiResponse<ProjectScrapSummary>>(`/api/v1/projects/${projectId}/scrap-summary`)).data,
    enabled: canView && !!projectId && enabled,
    retry: false,
  });
}

/* ————————————————————————— Ticket writes ————————————————————————— */

/** Refresh every surface a ticket write can move: list, this detail, dashboards. */
function invalidateScrapWrites(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: ['scraptrack', 'tickets'] });
  qc.invalidateQueries({ queryKey: ['scraptrack', 'dashboard'] });
  if (id) qc.invalidateQueries({ queryKey: scrapKeys.ticket(id) });
}

/**
 * Create a truck ticket (perm `scraptrack.create_tickets`). 201 on create, 200
 * on a `local_uuid` replay; 409 when `nadc_ticket_number` already exists — the
 * caller surfaces that plainly.
 */
export function useCreateScrapTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ScrapTicketWriteData) =>
      (await api.post<ApiResponse<ScrapTicket>>(`${BASE}/tickets/truck`, data)).data,
    onSuccess: () => invalidateScrapWrites(qc),
  });
}

/** Update a truck ticket (perm `scraptrack.create_tickets`). */
export function useUpdateScrapTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: ScrapTicketWriteData & { id: number }) =>
      (await api.put<ApiResponse<ScrapTicket>>(`${BASE}/tickets/truck/${id}`, data)).data,
    onSuccess: (_res, vars) => invalidateScrapWrites(qc, String(vars.id)),
  });
}

/** Delete a truck ticket (perm `scraptrack.create_tickets`; `scraptrack.admin` for priced+). */
export function useDeleteScrapTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/tickets/truck/${id}`),
    onSuccess: (_res, id) => invalidateScrapWrites(qc, String(id)),
  });
}

/**
 * Bulk pricing (parity B3, perm `scraptrack.manage_pricing`): apply the matching
 * price sheet to every unpriced ticket of a job. Per-ticket "no matching sheet"
 * comes back in `errors`, not as a failure.
 */
export function useApplyAllScrapPricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { job_id: number }) =>
      (await api.post<ApiResponse<ScrapBulkPricingResult>>(`${BASE}/tickets/truck/apply-all-pricing`, data)).data,
    onSuccess: () => invalidateScrapWrites(qc),
  });
}

/** Record a buyer payment on a ticket (perm `scraptrack.manage_pricing`). */
export function useUpdateScrapTicketPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: ScrapPaymentData & { id: number }) =>
      (await api.patch<ApiResponse<ScrapTicket>>(`${BASE}/tickets/truck/${id}/payment`, data)).data,
    onSuccess: (_res, vars) => invalidateScrapWrites(qc, String(vars.id)),
  });
}
